import { z } from 'zod';
import {
    PortfolioAmountPart,
    PortfolioProductCharging,
    PortfolioProductConsumptionMoment,
    PortfolioProductOptions,
    PortfolioPromotions,
} from '@mews-commander/data-access';
import { MAX_ACCOUNT_NOTE_INPUT_LENGTH } from '@mews-commander/constants';
import { CustomErrorParams, CustomErrorType } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';

const OptionalStringSchema = z.string().optional();

export enum ConfigureProductFormStep {
    Details = 'details',
    Pricing = 'pricing',
    Taxes = 'taxes',
    Accounting = 'accounting',
    Services = 'services',
}

/**
 * Details step
 */
interface DefaultProductFormValues {
    image?: File[] | null;
    centralProductId: string;
}

export enum DetailsFieldName {
    ExternalName = 'externalName',
    Description = 'description',
    Image = 'image',
    ImageId = 'imageId',
    Languages = 'languages',
    ExternalIdentifier = 'externalIdentifier',
}

const MEGA_BYTE = 1024 * 1024;
export const PRODUCT_PHOTO_FILE_SIZE_LIMIT = 5 * MEGA_BYTE;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

type ProductImageFiles = Array<File | undefined> | null;

const handleValidFiles = (files: ProductImageFiles) => files?.filter((file): file is File => file !== undefined) || [];

export const detailsConfigurationSchema = z.object({
    [DetailsFieldName.Languages]: z.record(
        z
            .object({
                externalName: z.string().optional(),
                description: z.string().max(MAX_ACCOUNT_NOTE_INPUT_LENGTH).optional(),
            })
            .optional()
    ),
    [DetailsFieldName.Image]: z
        .instanceof(File)
        .array()
        .refine(
            (files: ProductImageFiles) => {
                const validFiles = handleValidFiles(files);
                return validFiles.length <= 1;
            },
            {
                params: {
                    errorType: CustomErrorType.Other,
                    textKey: TextKey.MultipleFileDropNotAllowedErrorMessage,
                } satisfies CustomErrorParams,
            }
        )
        .refine(
            (files: ProductImageFiles) => {
                const validFiles = handleValidFiles(files);
                return validFiles.length === 0 || validFiles[0].size <= PRODUCT_PHOTO_FILE_SIZE_LIMIT;
            },
            {
                params: { errorType: CustomErrorType.FileSize, maxSize: '5MB' } satisfies CustomErrorParams,
            }
        )
        .refine(
            (files: ProductImageFiles) => {
                const validFiles = handleValidFiles(files);
                return validFiles.length === 0 || validFiles.every((file) => ACCEPTED_IMAGE_TYPES.includes(file.type));
            },
            {
                params: {
                    errorType: CustomErrorType.Other,
                    textKey: TextKey.ConfigureProductDetailsProductImageFileTypeError,
                } satisfies CustomErrorParams,
            }
        )
        .optional(),
});

export type DetailsConfigurationFormData = z.infer<typeof detailsConfigurationSchema>;

export const updateDetailSchema = detailsConfigurationSchema.extend({
    [DetailsFieldName.ExternalIdentifier]: z.string().optional(),
    [DetailsFieldName.ImageId]: z.string().nullish(),
});

export type UpdateDetailsConfigurationFormData = z.infer<typeof updateDetailSchema>;

/**
 * Pricing step
 */
export enum PricingFieldNames {
    Price = 'price',
    CurrencyCode = 'currencyCode',
}

export const getBasePriceFieldName = (propertyId: string) =>
    `${ConfigureProductFormStep.Pricing}.${propertyId}.${PricingFieldNames.Price}`;

export const basePriceSchema = z.object({
    [PricingFieldNames.Price]: z.number().nonnegative(),
    [PricingFieldNames.CurrencyCode]: z.string(),
});

export const pricingSchema = z.object({ [ConfigureProductFormStep.Pricing]: z.record(basePriceSchema) });

/**
 * Taxes step
 */
export enum TaxesFieldNames {
    TaxEnvironmentCode = 'taxEnvironmentCode',
    TaxationCode = 'taxationCode',
    TaxRate = 'taxRate',
}

export const taxesSchema = z.object({
    [TaxesFieldNames.TaxEnvironmentCode]: z.record(z.object({ [TaxesFieldNames.TaxRate]: z.record(z.string()) })),
});

/**
 * Accounting step
 */

export enum AccountingFieldName {
    AccountingCategoryKey = 'accountingCategoryKey',
    RebateAccountingCategoryKey = 'rebateAccountingCategoryKey',
    CancellationAccountingCategoryKey = 'cancellationAccountingCategoryKey',
}

export const accountingConfigurationSchema = z.object({
    [AccountingFieldName.AccountingCategoryKey]: OptionalStringSchema,
    [AccountingFieldName.RebateAccountingCategoryKey]: OptionalStringSchema,
    [AccountingFieldName.CancellationAccountingCategoryKey]: OptionalStringSchema,
});

/**
 * Services step
 */

export enum ServicesFieldNames {
    Services = 'services',
    Charging = 'charging',
    ConsumptionMoment = 'consumptionMoment',
    Timing = 'timing',
    Options = 'options',
    Promotions = 'promotions',
    PriceAdjustmentType = 'priceAdjustmentType',
    Multiplier = 'multiplier',
    AmountPart = 'amountPart',
    BookablePeriod = 'bookablePeriod',
    ServiceType = 'serviceType',
    Value = 'value',
}

export enum AdjustmentType {
    None = 'none',
    FixedPriceAdjustments = 'fixedPriceAdjustments',
    PercentagePriceAdjustments = 'percentagePriceAdjustments',
}

export const fixedPriceAdjustmentSchema = z.object({
    currencyCode: z.string(),
    value: z.number().nonnegative(),
});

export const percentagePriceAdjustmentSchema = z.object({
    [ServicesFieldNames.AmountPart]: z.nativeEnum(PortfolioAmountPart),
    [ServicesFieldNames.Multiplier]: z.number(),
});

export const servicesConfigurationSchema = z.object({
    [ServicesFieldNames.Services]: z
        .record(
            z.object({
                [ServicesFieldNames.Charging]: z.nativeEnum(PortfolioProductCharging),
                [ServicesFieldNames.ConsumptionMoment]: z.union([
                    z.nativeEnum(PortfolioProductConsumptionMoment),
                    z.literal(''),
                ]),
                [ServicesFieldNames.Options]: z.array(z.nativeEnum(PortfolioProductOptions)).optional(),
                [ServicesFieldNames.Promotions]: z.array(z.nativeEnum(PortfolioPromotions)).optional(),
                [AdjustmentType.FixedPriceAdjustments]: z.record(fixedPriceAdjustmentSchema).optional(),
                [AdjustmentType.PercentagePriceAdjustments]: z.record(percentagePriceAdjustmentSchema).optional(),
            })
        )
        .optional(),
});

export const getCurrentServiceFieldName = (currentService: string) =>
    `${ServicesFieldNames.Services}.${currentService}`;

export const getServicesPropertiesFieldName = (
    currentService: string,
    adjustmentType: AdjustmentType,
    propertyId: string
) => `${getCurrentServiceFieldName(currentService)}.${adjustmentType}.${propertyId}`;

export const configureProductsSchema = detailsConfigurationSchema
    .merge(pricingSchema)
    .merge(taxesSchema)
    .merge(accountingConfigurationSchema)
    .merge(servicesConfigurationSchema);

export type CentralProductConfigureFormValues = z.input<typeof configureProductsSchema> & DefaultProductFormValues;
